import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  List,
  X,
  Sun,
  Moon,
  Phone,
  ChatCircleDots,
  ArrowRight,
  ArrowUpRight,
  CaretDown,
  EnvelopeSimple,
  MapPin,
  InstagramLogo,
  FacebookLogo,
  GoogleLogo,
  XLogo,
  YoutubeLogo,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { siteImg, useSiteHead } from "@/lib/siteContent";
import { MATERIALS } from "@/lib/siteImages";
import {
  COLLECTIONS_MENU,
  COMPANY_MENU,
  INVENTORY_MENU,
  TRADE_MENU,
  VIEWS,
} from "@/lib/siteMenu";
import { InventoryMega } from "@/components/site/InventoryMega";
import { ContactPanel, OPEN_CONTACT_EVENT } from "@/components/site/ContactPanel";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/inventory", label: "Inventory", menu: INVENTORY_MENU },
  { to: "/collections", label: "Collections", menu: COLLECTIONS_MENU },
  { to: "/services", label: "Services" },
  { to: "/trade", label: "Trade", menu: TRADE_MENU },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "Company", menu: COMPANY_MENU, match: ["/about", "/contact", "/resources"] },
];

// Collections dropdown — one column per group (Material → Edges, …)
// Merge the static "Material" group with a dynamic "Brands" group from site config.
const buildCollections = (site) => {
  const items = (site?.config?.brands || [])
    .filter((b) => b && b.enabled !== false && b.slug)
    .map((b) => ({
      slug: b.slug,
      to: `/collections/brands/${b.slug}`,
      label: b.name || b.slug,
      tag: b.tagline || "",
    }));
  return COLLECTIONS_MENU.map((g) =>
    g.slug === "brands" && items.length ? { ...g, items } : g,
  );
};

const PANEL =
  "absolute inset-x-0 top-full hidden border-t border-white/10 bg-[color:var(--s-dark)] text-white shadow-[0_40px_80px_-40px_rgba(0,0,0,0.7)] lg:block";
const CARD =
  "group flex items-center justify-between gap-3  border border-white/10 bg-white/[0.03] px-5 py-4 transition-colors duration-300 hover:border-[color:var(--s-gold-bright)] hover:bg-white/[0.06]";

const CollectionsMenu = ({ groups, onPick }) => (
  <div data-testid="site-nav-collections-menu" className={PANEL}>
    <div className="grid gap-10 px-6 py-10 lg:grid-cols-4 lg:px-12">
      {groups.flatMap((g) => {
        const chunks = [];
        for (let i = 0; i < g.items.length; i += 4)
          chunks.push(g.items.slice(i, i + 4));
        return chunks.map((items, ci) => (
          <div
            key={`${g.slug}-${ci}`}
            data-testid={`site-nav-collections-${g.slug}${ci ? `-${ci}` : ""}`}
          >
            <div className="s-micro text-[color:var(--s-gold-bright)]">
              {ci === 0 ? g.group : "\u00A0"}
            </div>
            <div className="mt-4 grid gap-2">
              {items.map((m) => (
                <Link
                  key={m.slug}
                  to={m.to}
                  onClick={onPick}
                  data-testid={`site-nav-collections-${g.slug}-${m.slug}`}
                  className={CARD}
                >
                  <span>
                    <span className="s-h block text-sm">{m.label}</span>
                    <span className="mt-1 block text-xs text-white/50">{m.tag}</span>
                  </span>
                  <ArrowUpRight
                    size={15}
                    weight="bold"
                    className="shrink-0 opacity-40 transition-opacity duration-300 group-hover:opacity-100"
                  />
                </Link>
              ))}
            </div>
          </div>
        ));
      })}
    </div>
  </div>
);

const SimpleMenu = ({ items, micro, testId, prefix, onPick }) => (
  <div data-testid={testId} className={PANEL}>
    <div className="grid gap-4 px-6 py-10 lg:grid-cols-3 lg:px-12">
      {items.map((m) => (
        <Link
          key={m.slug}
          to={m.to}
          onClick={onPick}
          data-testid={`${prefix}-${m.slug}`}
          className={`${CARD} !items-start !p-6`}
        >
          <span>
            <span className="s-micro block text-[color:var(--s-gold-bright)]">{micro}</span>
            <span className="s-h mt-3 block text-lg">{m.label}</span>
            <span className="mt-2 block text-sm text-white/55">{m.tag}</span>
          </span>
          <ArrowUpRight
            size={16}
            weight="bold"
            className="mt-1 shrink-0 opacity-40 transition-opacity duration-300 group-hover:opacity-100"
          />
        </Link>
      ))}
    </div>
  </div>
);

export const useSite = () => {
  const [site, setSite] = useState(null);
  useEffect(() => {
    fetch(`${API}/public/site`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setSite)
      .catch(() => setSite(null));
  }, []);
  return site;
};

const MobileGroup = ({ title, children, testId }) => (
  <div className="border-b border-white/10 py-4" data-testid={testId}>
    {title && (
      <div className="s-micro px-1 py-2 text-[color:var(--s-gold-bright)]">{title}</div>
    )}
    {children}
  </div>
);

const MobileCard = ({ to, onClick, testId, label, tag, micro }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    className="flex items-center justify-between  border border-white/10 px-5 py-4"
  >
    <span>
      {micro && (
        <span className="s-micro block text-[color:var(--s-gold-bright)]">{micro}</span>
      )}
      <span className="s-h block text-base">{label}</span>
      {tag && <span className="mt-1 block text-xs text-white/50">{tag}</span>}
    </span>
    <ArrowUpRight size={16} weight="bold" className="shrink-0 text-[color:var(--s-gold-bright)]" />
  </Link>
);

export const SiteHeader = ({ site, overHero = false, theme = "light", onToggleTheme }) => {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(false);
  const [sub, setSub] = useState("");
  const [solid, setSolid] = useState(!overHero);
  const shop = site?.shop || {};

  useEffect(() => {
    const onOpen = () => setPanel(true);
    window.addEventListener(OPEN_CONTACT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONTACT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!overHero) return;
    const onScroll = () => setSolid(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overHero]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const transparent = overHero && !solid && !sub;
  const onDark = true;
  const ink = onDark ? "text-white" : "text-[color:var(--s-ink)]";
  const muted = onDark ? "text-white/75 hover:text-white" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]";
  const logos = site?.config?.branding || {};
  const headerLogo = onDark
    ? logos.logo_regular_light || logos.logo_sticky_light || logos.logo_regular_dark || logos.logo_sticky_dark
    : logos.logo_sticky_dark || logos.logo_regular_dark || logos.logo_sticky_light || logos.logo_regular_light;

  return (
    <>
      <header
        data-testid="site-header"
        onMouseLeave={() => setSub("")}
        className={`${overHero ? "fixed" : "sticky"} inset-x-0 top-0 z-50 px-6 transition-[background-color,border-color,color] duration-500 lg:px-12 ${
          transparent ? "bg-transparent text-white" : "s-header-solid text-white"
        }`}
      >
        <div className="flex w-full items-center gap-8 py-4">
          <Link to="/" data-testid="site-logo" className={`flex items-center gap-3 ${ink}`}>
            {headerLogo ? (
              <img
                src={siteImg(headerLogo)}
                alt={shop.name || "Brioni Granite"}
                data-testid="site-header-logo"
                className="h-10 w-auto max-w-[220px] object-contain"
              />
            ) : shop.has_logo ? (
              <img
                src={`${API}/public/logo`}
                alt={shop.name || "Brioni Granite"}
                data-testid="site-header-logo"
                className={`h-9 w-auto max-w-[220px] object-contain ${onDark ? "brightness-0 invert" : ""}`}
              />
            ) : (
              <span className="s-h text-base tracking-[0.2em]">
                {shop.name || "Brioni Granite"}
              </span>
            )}
          </Link>

          <nav
            data-testid="site-nav"
            className="mx-auto hidden h-12 items-center gap-5 lg:flex xl:gap-7"
          >
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onMouseEnter={() => setSub(n.menu ? n.label : "")}
                data-testid={`site-nav-${n.label.toLowerCase()}`}
                className={({ isActive: routeActive }) => {
                  const isActive =
                    routeActive ||
                    (n.match || []).some((p) => window.location.pathname.startsWith(p));
                  return `s-micro relative flex items-center gap-1.5 py-2 !text-[12px] transition-colors duration-300 after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:scale-x-0 after:bg-[color:var(--s-gold-bright)] after:transition-transform after:duration-300 hover:after:scale-x-100 ${
                    isActive ? `${ink} after:scale-x-100` : muted
                  }`;
                }}
              >
                {n.label}
                {n.menu && (
                  <CaretDown
                    size={10}
                    weight="bold"
                    className={`transition-transform duration-300 ${
                      sub === n.label ? "rotate-180" : ""
                    }`}
                  />
                )}
              </NavLink>
            ))}
          </nav>

          {shop.phone && (
            <a
              href={`tel:${shop.phone_link || shop.phone}`}
              data-testid="site-header-phone"
              className={`s-micro hidden items-center gap-2 whitespace-nowrap !text-[12px] transition-colors duration-300 2xl:flex ${muted}`}
            >
              <Phone size={14} weight="bold" /> {shop.phone}
            </a>
          )}

          <span className="hidden md:block lg:hidden xl:block">
            <Link
              to="/contact"
              data-testid="site-header-quote"
              className="s-btn s-btn-pill whitespace-nowrap"
            >
              Free quote <ArrowRight size={13} weight="bold" />
            </Link>
          </span>

          <button
            type="button"
            data-testid="site-theme-toggle"
            onClick={onToggleTheme}
            aria-label="Switch between light and dark"
            className="hidden h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white transition-colors duration-300 hover:bg-white hover:text-[color:var(--s-ink)] lg:flex"
          >
            {theme === "dark" ? <Sun size={17} weight="bold" /> : <Moon size={17} weight="bold" />}
          </button>

          <button
            type="button"
            data-testid="site-contact-toggle"
            onClick={() => setPanel(true)}
            aria-label="Get in touch"
            className={`hidden h-11 w-11 items-center justify-center rounded-full border transition-colors duration-300 lg:flex ${
              onDark
                ? "border-white/30 text-white hover:bg-white hover:text-[color:var(--s-ink)]"
                : "border-[color:var(--s-line)] text-[color:var(--s-ink)] hover:bg-[color:var(--s-ink)] hover:text-white"
            }`}
          >
            <List size={18} weight="regular" />
          </button>

          <div className="ml-auto flex items-center gap-3 lg:hidden">
            <button
              type="button"
              data-testid="site-theme-toggle-mobile"
              onClick={onToggleTheme}
              aria-label="Switch between light and dark"
              className={`flex h-9 w-9 items-center justify-center rounded-full border border-white/30 ${ink}`}
            >
              {theme === "dark" ? <Sun size={15} weight="bold" /> : <Moon size={15} weight="bold" />}
            </button>
            <button
              type="button"
              data-testid="site-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className={`s-micro flex items-center gap-2 ${ink}`}
            >
              {open ? (
                <>
                  Close <X size={16} weight="bold" />
                </>
              ) : (
                <>
                  Menu <List size={16} weight="bold" />
                </>
              )}
            </button>
          </div>
        </div>

        {sub === "Inventory" && <InventoryMega onPick={() => setSub("")} />}
        {sub === "Trade" && (
          <SimpleMenu
            items={TRADE_MENU}
            micro="Trade program"
            testId="site-nav-trade-menu"
            prefix="site-nav-trade"
            onPick={() => setSub("")}
          />
        )}
        {sub === "Company" && (
          <SimpleMenu
            items={COMPANY_MENU}
            micro="Company"
            testId="site-nav-company-menu"
            prefix="site-nav-company"
            onPick={() => setSub("")}
          />
        )}
        {sub === "Collections" && (
          <CollectionsMenu groups={buildCollections(site)} onPick={() => setSub("")} />
        )}
      </header>

      <ContactPanel open={panel} onClose={() => setPanel(false)} site={site} />

      {open && (
        <div
          className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-[color:var(--s-dark)] px-6 pb-12 pt-5 text-white lg:hidden"
          data-testid="site-mobile-menu"
        >
          <div className="flex items-center justify-between">
            <span className="s-h text-base tracking-[0.2em]">
              {shop.name || "Brioni Granite"}
            </span>
            <button
              type="button"
              data-testid="site-mobile-close"
              onClick={() => setOpen(false)}
              className="s-micro flex items-center gap-2"
            >
              Close <X size={16} weight="bold" />
            </button>
          </div>
          <div className="mt-8">
            {NAV.map((n) => (
              <div key={n.to}>
                <Link
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`site-mobile-nav-${n.label.toLowerCase()}`}
                  className="s-h flex items-center justify-between border-b border-white/10 py-5 text-2xl"
                >
                  {n.label}
                  <ArrowUpRight size={16} weight="bold" className="opacity-40" />
                </Link>
                {n.menu && n.label === "Collections" && (
                  <MobileGroup testId="site-mobile-collections-menu">
                    {buildCollections(site).map((g) => (
                      <div key={g.slug} className="grid gap-2 pb-3">
                        <div className="s-micro px-1 py-2 text-[color:var(--s-gold-bright)]">
                          {g.group}
                        </div>
                        {g.items.map((m) => (
                          <MobileCard
                            key={m.slug}
                            to={m.to}
                            onClick={() => setOpen(false)}
                            testId={`site-mobile-collections-${g.slug}-${m.slug}`}
                            label={m.label}
                            tag={m.tag}
                          />
                        ))}
                      </div>
                    ))}
                  </MobileGroup>
                )}
                {n.menu && n.label === "Trade" && (
                  <MobileGroup testId="site-mobile-trade-menu">
                    <div className="grid gap-2">
                      {n.menu.map((m) => (
                        <MobileCard
                          key={m.slug}
                          to={m.to}
                          onClick={() => setOpen(false)}
                          testId={`site-mobile-trade-${m.slug}`}
                          micro="Trade program"
                          label={m.label}
                        />
                      ))}
                    </div>
                  </MobileGroup>
                )}
                {n.menu && n.label === "Company" && (
                  <MobileGroup testId="site-mobile-company-menu">
                    <div className="grid gap-2">
                      {n.menu.map((m) => (
                        <MobileCard
                          key={m.slug}
                          to={m.to}
                          onClick={() => setOpen(false)}
                          testId={`site-mobile-company-${m.slug}`}
                          label={m.label}
                          tag={m.tag}
                        />
                      ))}
                    </div>
                  </MobileGroup>
                )}
                {n.menu && n.label === "Inventory" && (
                  <MobileGroup testId="site-mobile-inventory-menu">
                    <div className="flex flex-wrap gap-2">
                      {n.menu
                        .slice(1)
                        .filter((m) => m.slug !== "remnants")
                        .map((m) => (
                          <Link
                            key={m.slug}
                            to={m.to}
                            onClick={() => setOpen(false)}
                            data-testid={`site-mobile-material-${m.slug}`}
                            className="s-micro rounded-full border border-white/15 px-4 py-2.5 text-white/70"
                          >
                            {m.label}
                          </Link>
                        ))}
                    </div>
                    <Link
                      to="/inventory?type=Remnant"
                      onClick={() => setOpen(false)}
                      data-testid="site-mobile-material-remnants"
                      className="mt-3 flex items-center justify-between  bg-[color:var(--s-panel)] px-5 py-4 text-white"
                    >
                      <span>
                        <span className="s-micro block text-[color:var(--s-gold-bright)]">
                          {VIEWS.remnant.micro}
                        </span>
                        <span className="s-h mt-1 block text-xl">Remnants</span>
                      </span>
                      <ArrowUpRight size={16} weight="bold" className="text-[color:var(--s-gold-bright)]" />
                    </Link>
                  </MobileGroup>
                )}
              </div>
            ))}
          </div>
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="s-btn mt-10 w-full"
            data-testid="site-mobile-quote"
          >
            Get a free quote
          </Link>
          {shop.phone && (
            <a
              href={`tel:${shop.phone_link || shop.phone}`}
              className="s-micro mt-8 flex items-center gap-2 text-white/60"
            >
              <Phone size={13} weight="bold" /> {shop.phone}
            </a>
          )}
          {(shop.sms_phone || shop.phone) && (
            <a
              href={`sms:${shop.sms_link || shop.phone_link || shop.phone}`}
              data-testid="site-menu-text"
              className="s-micro mt-3 flex items-center gap-2 text-white/60"
            >
              <ChatCircleDots size={13} weight="bold" /> Text {shop.sms_phone || shop.phone}
            </a>
          )}
        </div>
      )}
    </>
  );
};

const SOCIALS = [
  ["instagram", InstagramLogo, "Instagram"],
  ["twitter", XLogo, "X"],
  ["youtube", YoutubeLogo, "YouTube"],
  ["facebook", FacebookLogo, "Facebook"],
  ["google_reviews", GoogleLogo, "Google reviews"],
];

const DEFAULT_NAV_COLUMN = {
  heading: "Navigation",
  links: [
    { label: "Home", href: "/" },
    { label: "About us", href: "/about" },
    { label: "Gallery", href: "/gallery" },
    { label: "Inventory", href: "/inventory" },
    { label: "Services", href: "/services" },
    { label: "Trade", href: "/trade" },
    { label: "Resources", href: "/resources" },
    { label: "Contact", href: "/contact" },
  ],
};

// Footer links can point at an internal page (/inventory) or somewhere off-site
// (https://, mailto:, tel:). Portal + staff sign-in are separate bundles, so they
// always hard-navigate rather than route inside the marketing SPA.
const FootLink = ({ href = "/", children, testId, className }) => {
  const cls =
    className ||
    "block text-[15px] text-white/55 transition-colors duration-300 hover:text-[color:var(--s-gold-bright)]";
  const hard =
    /^(https?:|mailto:|tel:|#)/.test(href) ||
    href.startsWith("/portal") ||
    href.startsWith("/admin");
  if (hard) {
    const ext = /^https?:/.test(href);
    return (
      <a
        href={href}
        data-testid={testId}
        className={cls}
        {...(ext ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={href} data-testid={testId} className={cls}>
      {children}
    </Link>
  );
};

const FooterNewsletter = ({ foot }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setState("error");
      return;
    }
    setState("loading");
    try {
      const res = await fetch(`${API}/public/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source: "footer" }),
      });
      if (!res.ok) throw new Error("failed");
      setState("done");
      setEmail("");
    } catch {
      setState("error");
    }
  };
  const heading = foot.newsletter_heading || "Join the list";
  if (state === "done") {
    return (
      <div data-testid="site-newsletter-done">
        <div className="s-micro text-white/45">{heading}</div>
        <p className="mt-3 text-sm text-white/60">Thanks — you're on the list.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} data-testid="site-newsletter">
      <div className="s-micro text-white/45">{heading}</div>
      {foot.newsletter_subtext && (
        <p className="mt-2 max-w-xs text-sm leading-relaxed text-white/45">
          {foot.newsletter_subtext}
        </p>
      )}
      <div className="mt-4 flex max-w-sm items-center gap-2 rounded-full border border-white/15 py-1 pl-5 pr-1 transition-colors duration-300 focus-within:border-[color:var(--s-gold-bright)]">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state === "error") setState("idle");
          }}
          placeholder="you@email.com"
          data-testid="site-newsletter-email"
          className="min-w-0 flex-1 bg-transparent text-[15px] text-white placeholder:text-white/30 focus:outline-none"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="site-newsletter-submit"
          className="inline-flex h-10 shrink-0 items-center rounded-full border border-white/40 bg-transparent px-5 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:border-[color:var(--s-gold-bright)] hover:text-[color:var(--s-gold-bright)] disabled:opacity-40"
        >
          {state === "loading" ? "…" : foot.newsletter_button || "Subscribe"}
        </button>
      </div>
      {state === "error" && (
        <p className="mt-2 text-xs text-red-300" data-testid="site-newsletter-error">
          Enter a valid email address.
        </p>
      )}
    </form>
  );
};

const FooterCol = ({ heading, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testId}
      className="border-b border-white/10 py-3 sm:border-0 sm:py-0"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className="flex w-full items-center justify-between gap-3 sm:pointer-events-none"
      >
        <span className="s-eyebrow s-on-dark">{heading}</span>
        <CaretDown
          size={14}
          weight="bold"
          className={`shrink-0 text-white/50 transition-transform duration-300 sm:hidden ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`${open ? "mt-4 block" : "hidden"} sm:mt-6 sm:block`}>
        {children}
      </div>
    </div>
  );
};

export const SiteFooter = ({ site }) => {
  const shop = site?.shop || {};
  const foot = site?.config?.footer || {};
  const placeId = (site?.config?.google?.place_id || "").trim();
  const reviewUrl = placeId
    ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`
    : "";
  const socials = SOCIALS.filter(([key]) => foot[key]);
  const columns = foot.columns?.length ? foot.columns : [DEFAULT_NAV_COLUMN];
  const hasSurfaces = columns.some((c) =>
    /surface|material/i.test(c.heading || ""),
  );
  const liveMaterials = (site?.materials || [])
    .filter((m) => m.material)
    .slice(0, 6);
  const surfaces = (
    liveMaterials.length
      ? liveMaterials.map((m) => m.material)
      : MATERIALS.map((m) => m.name)
  ).map((name) => ({
    label: name,
    href: `/inventory?material=${encodeURIComponent(name)}`,
  }));
  const linkColumns = hasSurfaces
    ? columns
    : [...columns, { heading: "Surfaces", links: surfaces }];
  const legal = foot.legal_links?.length ? foot.legal_links : [];
  const brandName = shop.name || "Brioni Granite";
  const mark = (foot.watermark || brandName).trim();
  const logo = site?.config?.branding?.logo_regular_light;

  return (
    <footer
      className="site-footer s-dark-tex relative overflow-hidden text-white"
      data-testid="site-footer"
    >
      <div className="relative z-10 px-5 pb-12 pt-16 sm:px-6 lg:px-12 lg:pb-14 lg:pt-24">
        <div
          className="grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_repeat(var(--cols),1fr)_1.9fr] lg:gap-10"
          style={{ "--cols": linkColumns.length }}
        >
            {/* Brand + blurb + outline CTA */}
            <div data-testid="site-footer-brand">
              {logo ? (
                <img
                  src={siteImg(logo)}
                  alt={brandName}
                  className="h-10 w-auto max-w-[200px] object-contain"
                />
              ) : (
                <span className="s-h text-lg tracking-[0.18em]">{brandName}</span>
              )}
              <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/55">
                {foot.tagline ||
                  "A stone warehouse, fabrication shop and install crew under one roof in St. Louis. The slab you pick is the slab we cut and set."}
              </p>
              <FootLink
                href={foot.cta_href || "/contact"}
                testId="site-footer-cta"
                className="s-ghost-light s-sm mt-8"
              >
                {foot.cta_label || "Get in touch"}
              </FootLink>
            </div>

            {/* Link columns */}
            {linkColumns.map((col, ci) => (
              <FooterCol
                key={ci}
                heading={col.heading}
                testId={`site-footer-col-${ci}`}
              >
                <div className="space-y-3">
                  {(col.links || []).map((l, li) => (
                    <FootLink
                      key={li}
                      href={l.href || "/"}
                      testId={`site-footer-col-${ci}-link-${li}`}
                    >
                      {l.label}
                    </FootLink>
                  ))}
                </div>
              </FooterCol>
            ))}

            {/* Get in touch */}
            <FooterCol heading="Get in touch" testId="site-footer-contact">
              <div className="space-y-4 text-[15px] leading-relaxed">
                {shop.phone && (
                  <a
                    href={`tel:${shop.phone_link || shop.phone}`}
                    data-testid="site-footer-call"
                    className="flex items-start gap-3 text-white/70 transition-colors duration-300 hover:text-[color:var(--s-gold-bright)]"
                  >
                    <Phone size={17} className="mt-0.5 shrink-0 text-[color:var(--s-gold-bright)]" />
                    {shop.phone}
                  </a>
                )}
                {shop.email && (
                  <a
                    href={`mailto:${shop.email}`}
                    data-testid="site-footer-email"
                    className="flex items-start gap-3 break-words text-white/70 transition-colors duration-300 hover:text-[color:var(--s-gold-bright)]"
                  >
                    <EnvelopeSimple size={17} className="mt-0.5 shrink-0 text-[color:var(--s-gold-bright)]" />
                    {shop.email}
                  </a>
                )}
                {shop.address && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(shop.address)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="site-footer-map"
                    className="flex items-start gap-3 whitespace-pre-line text-white/70 transition-colors duration-300 hover:text-[color:var(--s-gold-bright)]"
                  >
                    <MapPin size={17} className="mt-0.5 shrink-0 text-[color:var(--s-gold-bright)]" />
                    {shop.address}
                  </a>
                )}
                {(shop.sms_phone || shop.phone) && (
                  <a
                    href={`sms:${shop.sms_link || shop.phone_link || shop.phone}`}
                    data-testid="site-footer-text"
                    className="flex items-start gap-3 text-sm text-white/45 transition-colors duration-300 hover:text-white"
                  >
                    <ChatCircleDots size={17} className="mt-0.5 shrink-0 text-[color:var(--s-gold-bright)]" />
                    Text {shop.sms_phone || shop.phone}
                  </a>
                )}
              </div>
              {reviewUrl && (
                <a
                  href={reviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="site-footer-review"
                  className="mt-7 inline-flex h-11 items-center gap-2.5 rounded-full border border-[color:var(--s-gold-bright)] px-6 text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[color:var(--s-gold-bright)] hover:text-[color:var(--s-ink)]"
                >
                  <GoogleLogo size={15} weight="bold" />
                  Leave a Google review
                </a>
              )}
            </FooterCol>
        </div>

        {foot.show_newsletter !== false && (
          <div className="mt-14 border-t border-white/10 pt-10 lg:mt-16">
            <FooterNewsletter foot={foot} />
          </div>
        )}

        {/* Bottom bar — copyright, quiet links, social squares */}
        <div className="mt-12 flex flex-col gap-6 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <p className="text-xs text-white/40">
              © {new Date().getFullYear()} {brandName}. All rights reserved.
            </p>
            {legal.map((l, i) => (
              <FootLink
                key={i}
                href={l.href || "/"}
                testId={`site-footer-legal-${i}`}
                className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/50 transition-colors duration-300 hover:text-white"
              >
                {l.label}
              </FootLink>
            ))}
            {foot.show_portal_link !== false && (
              <FootLink
                href="/portal"
                testId="site-portal-link"
                className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/50 transition-colors duration-300 hover:text-white"
              >
                Customer portal
              </FootLink>
            )}
            {foot.show_staff_login !== false && (
              <FootLink
                href="/admin"
                testId="site-staff-login"
                className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/30 transition-colors duration-300 hover:text-white"
              >
                Staff sign in
              </FootLink>
            )}
          </div>
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-2.5" data-testid="site-footer-socials">
              {socials.map(([key, Icon, label]) => (
                <a
                  key={key}
                  href={foot[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`site-social-${key}`}
                  className="flex h-10 w-10 items-center justify-center  bg-white/10 text-white transition-colors duration-300 hover:bg-[color:var(--s-gold-bright)] hover:text-[color:var(--s-ink)]"
                >
                  <Icon size={17} weight="fill" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Oversized brand wordmark bleeding off the bottom edge */}
      {foot.show_watermark !== false && mark && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 select-none overflow-hidden leading-none"
          aria-hidden="true"
          data-testid="site-footer-watermark"
        >
          <div className="s-h translate-y-[28%] whitespace-nowrap text-center text-[16vw] text-white/[0.035] lg:text-[10.5vw]">
            {mark}
          </div>
        </div>
      )}
    </footer>
  );
};

const ComingSoon = ({ site }) => {
  const shop = site?.shop || {};
  return (
    <div
      className="site s-dark-tex flex min-h-screen items-center justify-center px-6"
      data-testid="site-coming-soon"
    >
      <div className="w-full max-w-xl  border border-white/10 bg-[color:var(--s-panel)] p-12 text-center text-white">
        <div className="s-eyebrow s-on-dark">Coming soon</div>
        <h1 className="s-h mt-6 text-4xl">{shop.name || "Brioni Granite"}</h1>
        <p className="mt-6 text-sm leading-relaxed text-white/60">
          Our new website is on its way. In the meantime give the shop a call — we are
          happy to walk the racks with you.
        </p>
        {shop.phone && (
          <a
            href={`tel:${shop.phone_link || shop.phone}`}
            data-testid="coming-soon-phone"
            className="s-btn mt-10"
          >
            <Phone size={14} weight="bold" /> {shop.phone}
          </a>
        )}
        {shop.address && (
          <p className="mt-8 whitespace-pre-line text-xs leading-relaxed text-white/50">
            {shop.address}
          </p>
        )}
      </div>
    </div>
  );
};

export const SiteShell = ({
  children,
  site,
  testId = "site-page",
  overHero = false,
  wide = false,
}) => {
  const cfg = site?.config;
  useSiteHead(cfg, site?.shop);
  const cfgTheme = cfg?.appearance?.theme === "dark" ? "dark" : "light";
  const [userTheme, setUserTheme] = useState("");
  const [sysTheme, setSysTheme] = useState("");
  useEffect(() => {
    try {
      const s = localStorage.getItem("brioni-theme");
      if (s === "light" || s === "dark") {
        setUserTheme(s);
        return;
      }
    } catch (e) {
      /* ignore */
    }
    // First-time visitor: honour the device's dark-mode preference before the saved default.
    try {
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        setSysTheme("dark");
      }
    } catch (e) {
      /* ignore */
    }
  }, []);
  const theme = userTheme || sysTheme || cfgTheme;
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setUserTheme(next);
    try {
      localStorage.setItem("brioni-theme", next);
    } catch (e) {
      /* ignore */
    }
  };
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = theme === "dark" ? "#0b0b0b" : "#efece6";
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, [theme]);
  if (cfg && cfg.enabled === false) return <ComingSoon site={site} />;
  return (
    <div className={`site min-h-screen${theme === "dark" ? " s-theme-dark" : ""}${wide ? " s-wide" : ""}`} data-testid={testId}>
      <SiteHeader site={site} overHero={overHero} theme={theme} onToggleTheme={toggleTheme} />
      {children}
      <SiteFooter site={site} />
    </div>
  );
};
